import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import type { RefObject } from "react";
import HeroButton from "/app/src/components/HeroUi/Button";
import HeroSelect from "/app/src/components/HeroUi/Select";
import { InputNumber, Row, Col } from "antd";

// Minimal interface for AntD TextArea component instance used for ref unwrapping
interface AntdTextAreaInstance {
  resizableTextArea?: {
    textArea?: HTMLTextAreaElement | null;
  };
  focus?: (options?: unknown) => void;
  blur?: () => void;
}

/**
 * Resolve the underlying input/textarea DOM element from a ref that may reference
 * a native element or an Ant Design TextArea component instance.
 *
 * @param ref - React ref that may point to an `HTMLInputElement`, `HTMLTextAreaElement`,
 *              or an Ant Design TextArea instance with `resizableTextArea.textArea`.
 * @returns The native input/textarea element if resolvable; otherwise `null`.
 */
function resolveDomElement(
  ref?: RefObject<
    HTMLInputElement | HTMLTextAreaElement | AntdTextAreaInstance
  >,
): HTMLInputElement | HTMLTextAreaElement | null {
  const current = ref?.current;
  if (!current) return null;
  if (
    current instanceof HTMLInputElement ||
    current instanceof HTMLTextAreaElement
  ) {
    return current;
  }
  const maybeAntd = current as AntdTextAreaInstance;
  return maybeAntd.resizableTextArea?.textArea ?? null;
}

interface FormValues {
  value: string | undefined;
  path: string | undefined;
}

/**
 *
 * @returns DynamicButtons component
 */
export default function DynamicButtons({
  disabled = false,
  dataPull = false,
  targetInputRef,
}: {
  disabled?: boolean;
  dataPull?: boolean;
  targetInputRef?: RefObject<
    HTMLInputElement | HTMLTextAreaElement | AntdTextAreaInstance
  >;
}) {
  const { t } = useTranslation();

  const { setFieldValue, values } = useFormikContext<FormValues>();
  const [period, setPeriod] = useState<string>("last");
  const [quantity, setQuantity] = useState<number>(1);
  const [frequency, setFrequency] = useState<string>("day");
  const [dateTimeFormat, setDateTimeFormat] = useState<string>("%Y%m%d.0");

  const updateDateTimeFormat = useCallback(
    (e) => {
      setDateTimeFormat(e.target.value);
    },
    [setDateTimeFormat],
  );

  const updateQuantity = useCallback(
    (value: number) => {
      setQuantity(value);
    },
    [setQuantity],
  );

  const updatePeriod = useCallback(
    (e) => {
      setPeriod(e.target.value);
    },
    [setPeriod],
  );

  const updateFrequency = useCallback(
    (e) => {
      setFrequency(e.target.value);
    },
    [setFrequency],
  );

  const addField = useCallback(() => {
    const injection = `{${period}${quantity}${frequency}:${dateTimeFormat}}`;

    // Determine current string based on mode
    const currentValue = dataPull ? values.path ?? "" : values.value ?? "";

    // Resolve underlying DOM element (AntD TextArea exposes resizableTextArea.textArea)
    const domEl = resolveDomElement(targetInputRef);

    if (
      domEl &&
      typeof domEl.selectionStart === "number" &&
      typeof domEl.selectionEnd === "number"
    ) {
      const start = domEl.selectionStart ?? currentValue.length;
      const end = domEl.selectionEnd ?? start;
      const nextValue = `${currentValue.slice(0, start)}${injection}${currentValue.slice(end)}`;
      setFieldValue(dataPull ? "path" : "value", nextValue);
      // Optional: reposition cursor just after inserted token
      requestAnimationFrame(() => {
        try {
          domEl.focus();
          const newPos = start + injection.length;
          domEl.setSelectionRange(newPos, newPos);
        } catch (_) {
          /* ignore */
        }
      });
    } else {
      // Fallback: append to end
      setFieldValue(dataPull ? "path" : "value", `${currentValue}${injection}`);
    }
  }, [
    dataPull,
    values.value,
    values.path,
    setFieldValue,
    targetInputRef,
    period,
    quantity,
    frequency,
    dateTimeFormat,
  ]);

  return (
    <div className="mt-2">
      <Row gutter={16}>
        <Col span={4}>
          <HeroSelect
            value={period}
            defaultSelectedKeys={["last"]}
            onChange={updatePeriod}
            items={[
              { key: "last", label: "Last", value: "last" },
              { key: "next", label: "Next", value: "next" },
            ]}
            ariaLabel="Select period"
          />
        </Col>
        <Col span={2}>
          <InputNumber
            min={1}
            max={100}
            value={quantity}
            size="large"
            onChange={updateQuantity}
          />
        </Col>
        <Col span={4}>
          <HeroSelect
            value={frequency}
            defaultSelectedKeys={["day"]}
            onChange={updateFrequency}
            items={[
              { key: "day", label: "Day(s)", value: "day" },
              { key: "week", label: "Week(s)", value: "week" },
              { key: "month", label: "Month(s)", value: "month" },
            ]}
            ariaLabel="Select period"
          />
        </Col>
        <Col span={8}>
          <HeroSelect
            value={dateTimeFormat}
            defaultSelectedKeys={["%Y%m%d.0"]}
            onChange={updateDateTimeFormat}
            ariaLabel="Select date time format"
            items={[
              { label: "YYYYMMDD.0", value: "%Y%m%d.0", key: "%Y%m%d.0" },
              { label: "MM/DD/YYYY", value: "%m/%d/%Y", key: "%m/%d/%Y" },
              { label: "DD/MM/YYYY", value: "%d/%m/%Y", key: "%d/%m/%Y" },
              { label: "YYYY-MM-DD", value: "%Y-%m-%d", key: "%Y-%m-%d" },
              {
                label: "YYYY-MM-DD HH:MM:SS",
                value: "%Y-%m-%d %H:%M:%S",
                key: "%Y-%m-%d %H:%M:%S",
              },
              {
                label: "YYYY-MM-DDTHH:MM:SS.SSS",
                value: "%Y-%m-%d%H:%M:%S.%f",
                key: "%Y-%m-%d%H:%M:%S.%f",
              },
              {
                label: "YYYY-MM-DDTHH:MM:SSZ",
                value: "%Y-%m-%dT%H:%M:%SZ",
                key: "%Y-%m-%dT%H:%M:%SZ",
              },
              {
                label: "YYYY-MM-DD HH:MM:SS.SSSZ",
                value: "%Y-%m-%d %H:%M:%S.%fZ",
                key: "%Y-%m-%d %H:%M:%S.%fZ",
              },
              {
                value: "%Y-%m-%dT%H:%M:%S%z",
                label: "YYYY-MM-DDTHH:MM:SS+HH:MM",
                key: "%Y-%m-%dT%H:%M:%S%z",
              },
              { label: "Epoch (seconds)", value: "epoch", key: "epoch" },
              {
                label: "Epoch (milliseconds)",
                value: "epoch_ms",
                key: "epoch_ms",
              },
            ]}
          />
        </Col>
        <Col span={6}>
          <HeroButton
            isDisabled={disabled}
            onClick={addField}
            size="md"
            fullWidth
          >
            {t("translation:add")}
          </HeroButton>
        </Col>
      </Row>
    </div>
  );
}
